// Helpers PUROS sobre la lista de mensajes: qué se guarda en el caché y qué
// tiene algo para mostrar. Están acá, fuera del hook, para poder testearlos sin
// React ni DOM.

import type { AdjuntoChat, MensajeChatAtlas } from './tipos';

/**
 * La versión de un mensaje que va al caché de sesión: los adjuntos SIN su
 * contenido.
 *
 * POR QUÉ. `AdjuntoChat.datos` es el base64 crudo de la imagen (hasta 700 KB
 * decodificados ⇒ ~933 K caracteres, y hasta 3 por mensaje). Ese string queda
 * en el historial para siempre, así que serializar el estado completo costaba
 * megabytes de `JSON.stringify` + `sessionStorage.setItem` —las dos síncronas,
 * en el hilo principal— cada vez que se guardaba. Y una conversación con dos
 * mensajes con fotos al tope ya pasa la cuota de ~5 MB, con lo que el guardado
 * pasaba a fallar y borrar la clave una y otra vez.
 *
 * QUÉ SE PIERDE, A PROPÓSITO: después de un refresh, un adjunto de imagen del
 * historial ya no tiene con qué dibujar la miniatura (se muestra la ficha con
 * nombre y peso, ver `BurbujaMensaje`) y tampoco vuelve a viajar a la API
 * (`bloquesDeAdjuntos` saltea los adjuntos sin datos). Es el precio de que el
 * caché sea barato: el chat entero sigue EN MEMORIA mientras no se refresque.
 */
export function mensajesParaCache(mensajes: MensajeChatAtlas[]): MensajeChatAtlas[] {
  return mensajes.map((m) => {
    if (!m.adjuntos || m.adjuntos.length === 0) return m;
    const adjuntos: AdjuntoChat[] = m.adjuntos.map((a) =>
      a.datos ? { ...a, datos: '' } : a,
    );
    return { ...m, adjuntos };
  });
}

/**
 * ¿Esta burbuja del asistente dibuja algo?
 *
 * Una ronda que sólo llamó herramientas sin salida visible (un nombre de tool
 * que no existe, un gráfico con input inválido), o un "detener" antes del
 * primer token, dejaban una burbuja sin texto, sin gráficos y sin sugerencias:
 * en pantalla, un hueco de ~20px sin ninguna explicación.
 */
export function tieneContenidoVisible(mensaje: MensajeChatAtlas): boolean {
  if (mensaje.texto.trim().length > 0) return true;
  if ((mensaje.graficos?.length ?? 0) > 0) return true;
  if ((mensaje.sugerencias?.length ?? 0) > 0) return true;
  if ((mensaje.adjuntos?.length ?? 0) > 0) return true;
  return false;
}

/** ¿Es una burbuja de asistente vacía, de las que hay que sacar del hilo? */
export const esBurbujaVacia = (mensaje: MensajeChatAtlas): boolean =>
  mensaje.rol === 'assistant' && !mensaje.error && !tieneContenidoVisible(mensaje);

/**
 * Dónde va el aviso de error del cliente: reusando la última burbuja o como una
 * burbuja nueva al final.
 *
 * Se reusa SÓLO si la última es del asistente y no dibuja nada — la de la ronda
 * que quedó a medio llenar. Se pregunta por contenido VISIBLE y no por "¿tiene
 * texto?": el loop de herramientas puede dejar una burbuja con sólo un gráfico,
 * sin texto, y seguir a otra ronda; si esa ronda falla ANTES de crear su propia
 * burbuja, la última del hilo es la del gráfico. Pisarla lo borra por partida
 * doble — `BurbujaMensaje` corta en `error` y nunca llega a dibujar los
 * gráficos, y `historialParaApi` saltea los `error`, así que el modelo también
 * pierde que lo había generado.
 */
export function hiloConError(
  mensajes: MensajeChatAtlas[],
  texto: string,
  nuevoId: () => string,
): MensajeChatAtlas[] {
  const ultimo = mensajes[mensajes.length - 1];
  if (ultimo && ultimo.rol === 'assistant' && !tieneContenidoVisible(ultimo)) {
    return [...mensajes.slice(0, -1), { ...ultimo, texto, error: true }];
  }
  return [...mensajes, { id: nuevoId(), rol: 'assistant', texto, error: true }];
}
